/**
 * Shopify Product Card Widget
 * Embeddable widget that displays a product card from a Shopify product URL
 * 
 * Usage:
 * 1. Add this script to your HTML:
 *    <script src="shopify-product-card.js"></script>
 * 
 * 2. Add a container with data attributes:
 *    <div class="shopify-product-card" 
 *         data-product-url="https://your-store.myshopify.com/products/product-handle"
 *         data-store-domain="your-store.myshopify.com">
 *    </div>
 */

class ShopifyProductCard {
  constructor(container) {
    this.container = container;
    this.productUrl = container.dataset.productUrl;
    this.storeDomain = container.dataset.storeDomain;
    
    if (!this.productUrl || !this.storeDomain) {
      this.showError('Missing data-product-url or data-store-domain attribute');
      return;
    }

    this.init();
  }

  async init() {
    try {
      const productData = await this.fetchProductData();
      this.render(productData);
    } catch (error) {
      this.showError(error.message);
    }
  }

  async fetchProductData() {
    // Extract product handle from URL
    const urlParts = this.productUrl.split('/products/');
    if (urlParts.length !== 2) {
      throw new Error('Invalid Shopify product URL');
    }
    
    const handle = urlParts[1].split('?')[0].split('#')[0];
    
    // Fetch from Shopify Storefront API
    const query = `
      {
        productByHandle(handle: "${handle}") {
          id
          title
          handle
          description
          featuredImage {
            url
            altText
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                availableForSale
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${this.storeDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.getAccessToken(),
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product data: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(data.errors[0].message);
    }

    if (!data.data.productByHandle) {
      throw new Error('Product not found');
    }

    return data.data.productByHandle;
  }

  getAccessToken() {
    // The access token should be set via window variable or data attribute
    // For security, never hardcode public tokens; use environment variables
    return window.SHOPIFY_STOREFRONT_TOKEN || this.container.dataset.accessToken;
  }

  render(product) {
    const minPrice = product.priceRange.minVariantPrice.amount;
    const maxPrice = product.priceRange.maxVariantPrice.amount;
    const currency = product.priceRange.minVariantPrice.currencyCode;
    const isAvailable = product.variants.edges[0]?.node?.availableForSale ?? true;

    const priceDisplay = minPrice === maxPrice 
      ? `${currency} ${parseFloat(minPrice).toFixed(2)}`
      : `${currency} ${parseFloat(minPrice).toFixed(2)} - ${parseFloat(maxPrice).toFixed(2)}`;

    const html = `
      <div class="spc-card">
        <div class="spc-image-container">
          <div class="spc-badge">Featured</div>
          ${product.featuredImage ? `
            <img 
              src="${product.featuredImage.url}?width=400&height=400&crop=center" 
              alt="${product.featuredImage.altText || product.title}"
              class="spc-image"
              loading="lazy"
            />
          ` : '<div class="spc-image-placeholder">No image</div>'}
        </div>
        
        <div class="spc-content">
          <h3 class="spc-title">${this.escapeHtml(product.title)}</h3>
          
          ${product.variants && product.variants.edges ? `
            <div class="spc-variant-selector">
              <select class="spc-variant-select" onchange="window.ShopifyProductCard.updateVariant(this)">
                ${product.variants.edges.map((v, i) => `
                  <option value="${v.node.id}" data-price="${v.node.priceV2?.amount || v.node.price?.amount || '0'}">
                    ${v.node.title || 'Option ' + (i + 1)}
                  </option>
                `).join('')}
              </select>
            </div>
          ` : ''}
          
          <p class="spc-price" data-base-price="${minPrice}">${priceDisplay}</p>
          
          <div class="spc-actions">
            <a href="${this.productUrl}" target="_blank" class="spc-btn spc-btn-primary">
              View Product
            </a>
            <button 
              class="spc-btn spc-btn-secondary spc-add-to-cart" 
              data-variant-id="${product.variants.edges[0]?.node?.id || ''}"
              onclick="window.ShopifyProductCard.addToCart(this.dataset.variantId)"
              ${!isAvailable ? 'disabled' : ''}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    `;

    this.container.innerHTML = html;
    this.container.style.display = 'block';
  }

  showError(message) {
    this.container.innerHTML = `
      <div class="spc-error">
        <p>Unable to load product</p>
        <small>${this.escapeHtml(message)}</small>
      </div>
    `;
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  static addToCart(variantId) {
    if (!variantId) {
      alert('Unable to add to cart: product variant not found');
      return;
    }
    
    // Convert gid format to numeric ID if needed
    let numericId = variantId;
    if (variantId.includes('gid://')) {
      numericId = variantId.split('/').pop();
    }
    
    // Add to cart using Shopify's standard cart endpoint
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: numericId,
            quantity: 1,
          }
        ]
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Cart API returned ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Fetch updated cart to get latest cart data
      return fetch('/cart.js').then(r => r.json()).then(cart => {
        // Trigger Prestige theme cart drawer refresh (MUST dispatch on documentElement with bubbles: true)
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: cart
        }));

        // Optional: Auto-open cart drawer if toggle button exists
        const cartToggle = document.querySelector('[data-action="toggle-mini-cart"]');
        if (cartToggle && !cartToggle.getAttribute('aria-expanded')) {
          cartToggle.click();
        }

        // Dispatch custom event for other integrations
        window.dispatchEvent(new CustomEvent('shopify-product-card:added', { detail: data }));

        return data;
      });
    })
    .then(data => {
      // Show a confirmation message on the button
      const buttons = document.querySelectorAll('.spc-add-to-cart');
      buttons.forEach(button => {
        if (button.dataset.variantId === variantId) {
          const originalText = button.textContent;
          button.textContent = 'Added!';
          button.disabled = true;
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
        }
      });
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart: ' + error.message);
    });
  }

  static updateVariant(selectElement) {
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const newPrice = selectedOption.dataset.price;
    const newVariantId = selectedOption.value;

    // Update the price display
    const priceElement = selectElement.closest('.spc-content').querySelector('.spc-price');
    if (priceElement && newPrice) {
      const currency = priceElement.textContent.split(' ')[0];
      priceElement.textContent = `${currency} ${parseFloat(newPrice).toFixed(2)}`;
    }

    // Update the add to cart button with new variant ID
    const addToCartBtn = selectElement.closest('.spc-content').querySelector('.spc-add-to-cart');
    if (addToCartBtn) {
      addToCartBtn.dataset.variantId = newVariantId;
    }
  }
}

// Initialize all product cards on page
function initializeShopifyProductCards() {
  const containers = document.querySelectorAll('[data-shopify-product-card]');
  containers.forEach(container => {
    new ShopifyProductCard(container);
  });
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeShopifyProductCards);
} else {
  initializeShopifyProductCards();
}

// Export for use in other contexts
window.ShopifyProductCard = ShopifyProductCard;
